import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Save, Calculator as CalcIcon, PackagePlus } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import api, { apiError } from "@/lib/api";
import { computeRecipe } from "@/lib/calc";
import { rupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const CATEGORIES = ["Makanan", "Minuman", "Kerajinan", "Jasa", "Lainnya"];
const emptyMaterial = () => ({ name: "", unit: "pcs", price_per_unit: 0, quantity: 1 });

export default function Kalkulator() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editing = location.state?.recipe || null;

  const [name, setName] = useState(editing?.name || "");
  const [category, setCategory] = useState(editing?.category || "Makanan");
  const [materials, setMaterials] = useState(editing?.materials?.length ? editing.materials : [emptyMaterial()]);
  const [ops, setOps] = useState(editing?.operational_costs?.length ? editing.operational_costs : [{ name: "Listrik & Gas", amount: 0 }]);
  const [units, setUnits] = useState(editing?.units_produced || 1);
  const [margin, setMargin] = useState(editing?.margin_percent ?? 40);
  const [saving, setSaving] = useState(false);

  const { data: dbMaterials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await api.get("/materials")).data,
  });

  const result = useMemo(
    () => computeRecipe({ materials, operational_costs: ops, units_produced: units, margin_percent: margin }),
    [materials, ops, units, margin]
  );

  const pieData = [
    { name: "Modal Bahan", value: Math.round(result.materialCost) },
    { name: "Biaya Operasional", value: Math.round(result.opCost) },
  ];
  const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))"];

  const setMat = (i, patch) => setMaterials((arr) => arr.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const setOp = (i, patch) => setOps((arr) => arr.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  const pickMaterial = (i, id) => {
    const m = dbMaterials.find((x) => x.id === id);
    if (m) setMat(i, { name: m.name, unit: m.unit, price_per_unit: m.price_per_unit });
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Nama produk wajib diisi.");
    if (Number(units) <= 0) return toast.error("Jumlah produksi harus lebih dari 0.");
    if (materials.some((m) => !m.name.trim())) return toast.error("Lengkapi nama semua bahan.");
    const payload = {
      name: name.trim(), category,
      materials: materials.map((m) => ({
        name: m.name, unit: m.unit, price_per_unit: Number(m.price_per_unit) || 0, quantity: Number(m.quantity) || 0,
      })),
      operational_costs: ops.filter((o) => o.name.trim()).map((o) => ({ name: o.name, amount: Number(o.amount) || 0 })),
      units_produced: Number(units) || 1, margin_percent: Number(margin) || 0,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/recipes/${editing.id}`, payload);
      else await api.post("/recipes", payload);
      await qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editing ? "Resep diperbarui." : "Resep tersimpan.");
      navigate("/resep");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Kalkulator HPP & Harga Jual</h1>
          <p className="text-sm text-muted-foreground">Hitung modal produksi, harga jual ideal, dan keuntungan otomatis.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="hpp-save-button" className="rounded-xl min-h-[44px]">
          <Save className="mr-2 h-4 w-4" /> {editing ? "Perbarui Resep" : "Simpan Resep"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Inputs */}
        <div className="space-y-5 lg:col-span-7">
          <Card className="rounded-2xl p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Nama Produk / Jasa</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="cth: Es Kopi Susu"
                  data-testid="hpp-product-name-input" className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label>Kategori</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="rounded-xl" data-testid="hpp-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Bahan Baku & Kemasan</h3>
              <Button variant="secondary" size="sm" className="rounded-lg" data-testid="hpp-add-material-button"
                onClick={() => setMaterials((a) => [...a, emptyMaterial()])}>
                <Plus className="mr-1 h-4 w-4" /> Bahan
              </Button>
            </div>
            <div className="space-y-3">
              {materials.map((m, i) => (
                <div key={i} className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`hpp-material-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-12 sm:col-span-5">
                      {dbMaterials.length > 0 && (
                        <Select onValueChange={(v) => pickMaterial(i, v)}>
                          <SelectTrigger className="mb-2 h-9 rounded-lg text-xs" data-testid="hpp-raw-material-select">
                            <SelectValue placeholder="Pilih dari daftar bahan..." />
                          </SelectTrigger>
                          <SelectContent>
                            {dbMaterials.map((dm) => <SelectItem key={dm.id} value={dm.id}>{dm.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                      <Input value={m.name} onChange={(e) => setMat(i, { name: e.target.value })}
                        placeholder="Nama bahan" className="h-9 rounded-lg" />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Input value={m.unit} onChange={(e) => setMat(i, { unit: e.target.value })}
                        placeholder="satuan" className="h-9 rounded-lg" />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Input type="number" value={m.price_per_unit} onChange={(e) => setMat(i, { price_per_unit: e.target.value })}
                        placeholder="Harga/satuan" className="h-9 rounded-lg font-mono" />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Input type="number" value={m.quantity} onChange={(e) => setMat(i, { quantity: e.target.value })}
                        placeholder="Jumlah" className="h-9 rounded-lg font-mono" />
                    </div>
                    <div className="col-span-1 flex items-center justify-end">
                      <button onClick={() => setMaterials((a) => a.filter((_, idx) => idx !== i))}
                        className="text-muted-foreground hover:text-destructive" data-testid={`hpp-remove-material-${i}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <p className="mt-1.5 text-right text-xs text-muted-foreground">
                    Subtotal: <span className="font-mono font-medium text-foreground">{rupiah((Number(m.price_per_unit) || 0) * (Number(m.quantity) || 0))}</span>
                  </p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Biaya Operasional (per batch)</h3>
              <Button variant="secondary" size="sm" className="rounded-lg" data-testid="hpp-add-op-button"
                onClick={() => setOps((a) => [...a, { name: "", amount: 0 }])}>
                <Plus className="mr-1 h-4 w-4" /> Biaya
              </Button>
            </div>
            <div className="space-y-2">
              {ops.map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input value={o.name} onChange={(e) => setOp(i, { name: e.target.value })}
                    placeholder="cth: Listrik, Sewa, Gaji" className="col-span-7 h-9 rounded-lg" />
                  <Input type="number" value={o.amount} onChange={(e) => setOp(i, { amount: e.target.value })}
                    placeholder="Rp" className="col-span-4 h-9 rounded-lg font-mono" />
                  <button onClick={() => setOps((a) => a.filter((_, idx) => idx !== i))}
                    className="col-span-1 flex items-center justify-center text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl p-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Jumlah Produksi (unit per batch)</Label>
                <Input type="number" value={units} onChange={(e) => setUnits(e.target.value)}
                  className="rounded-xl font-mono" data-testid="hpp-units-input" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Markup (% dari HPP)</Label>
                  <span className="font-mono text-sm font-bold text-primary">{margin}%</span>
                </div>
                <Slider value={[Number(margin)]} min={0} max={200} step={5}
                  onValueChange={(v) => setMargin(v[0])} className="py-2" data-testid="hpp-margin-slider" />
                <p className="text-xs text-muted-foreground">
                  Markup = keuntungan dibagi HPP. Contoh: HPP Rp10.000 dengan markup 30% menghasilkan harga jual Rp13.000.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Outputs */}
        <div className="lg:col-span-5">
          <Card className="sticky top-20 rounded-2xl border-primary/20 bg-gradient-to-b from-primary/[0.04] to-transparent p-5">
            <div className="flex items-center gap-2">
              <CalcIcon className="h-5 w-5 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Hasil Perhitungan</h3>
            </div>

            <div className="mt-4 space-y-3">
              <div className="rounded-xl bg-card p-4 shadow-sm" data-testid="hpp-result-hpp">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">HPP per Unit</p>
                <p className="font-mono text-2xl font-extrabold">{rupiah(result.hpp)}</p>
              </div>
              <div className="rounded-xl bg-primary p-4 text-primary-foreground shadow-sm" data-testid="hpp-result-price">
                <p className="text-xs uppercase tracking-wider opacity-80">Rekomendasi Harga Jual</p>
                <p className="font-mono text-3xl font-extrabold">{rupiah(result.price)}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-card p-4 shadow-sm" data-testid="hpp-result-profit-unit">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Untung / Unit</p>
                  <p className="font-mono text-lg font-bold text-primary">{rupiah(result.profitUnit)}</p>
                </div>
                <div className="rounded-xl bg-card p-4 shadow-sm" data-testid="hpp-result-profit-batch">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Untung / Batch</p>
                  <p className="font-mono text-lg font-bold text-primary">{rupiah(result.profitBatch)}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-card p-4 shadow-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Struktur Biaya</p>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" innerRadius={38} outerRadius={62} paddingAngle={3}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => rupiah(v)} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2 text-sm">
                <span className="text-muted-foreground">Total Modal / Batch</span>
                <span className="font-mono font-bold">{rupiah(result.total)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
